/**
 * Add-by-type / self-reported preferred facilities (no NPI).
 * Checkout and the facility selector historically required NPI / leftover PK
 * fields that add-by-type never writes, so a purchased AFH vanished after pay.
 */

import type { UserPreferredFacility } from "@services/payment";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import { isSameNameAndAddress, type NameAddressFacility } from "./addFacilityByType";
import type { PostPurchaseFacilitySelection } from "./postPurchaseFacilitySelection";

export const SELF_REPORTED_VERIFICATION_SOURCE = "self_reported";

const CONTENT_SLUG_ALIASES: Record<string, string> = {
  // Curated AFH copy: shared residential / assisted living pack until Rules
  // publishes a dedicated adult_family_home SOM pack.
  adult_family_home: "assisted_living_facility",
};

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function nonemptyString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function finiteId(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function preferredDetailsRecord(
  facility: unknown,
): Record<string, unknown> {
  if (!facility || typeof facility !== "object") return {};
  return asRecord((facility as { facility_details?: unknown }).facility_details);
}

/** True when the row was created via add-by-type / skip-NPI self-report. */
export function isSelfReportedPreferredFacility(facility: unknown): boolean {
  if (!facility || typeof facility !== "object") return false;
  const row = facility as Record<string, unknown>;
  const details = preferredDetailsRecord(facility);
  if (row.skip_npi_validation === true || details.skip_npi_validation === true) {
    return true;
  }
  const source = nonemptyString(row.verification_source || details.verification_source);
  return source === SELF_REPORTED_VERIFICATION_SOURCE;
}

/**
 * User-preferred-facilities PK for Stripe / Rules checkout.
 * Leftover unpaid rows stash it on `user_preferred_facility_id` (do not use
 * `id` there — leftover `id` can be the Rules facility_id).
 * Add-by-type session blobs only have `id` as the UPF PK.
 */
export function resolveCheckoutPreferredFacilityPk(
  facility: unknown,
): number | null {
  if (!facility || typeof facility !== "object") return null;
  const row = facility as Record<string, unknown>;
  const explicit = finiteId(row.user_preferred_facility_id);
  if (explicit != null) return explicit;
  if (!isSelfReportedPreferredFacility(facility)) return null;
  return finiteId(row.id);
}

export function selfReportedLicenseNumber(facility: unknown): string {
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  return (
    nonemptyString(row.state_license_number) ||
    nonemptyString(details.state_license_number) ||
    nonemptyString(row.license_number) ||
    nonemptyString(details.license_number)
  );
}

export function selfReportedFacilityTypeSlug(facility: unknown): string {
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  return (
    nonemptyString(row.facility_type_slug) ||
    nonemptyString(details.facility_type_slug)
  );
}

/**
 * Rules-safe unique id (`prefix_digits`) when there is no NPI.
 * Stable across checkout retries so webhook attach does not twin-mint.
 */
export function selfReportedUniqueId(
  preferredPk?: number | null,
  license?: string | null,
): string {
  const digits = String(license || "").replace(/\D/g, "");
  if (digits) return `license_${digits}`;
  if (preferredPk && preferredPk > 0) return `selfreported_${preferredPk}`;
  return "";
}

/**
 * Discover / SOM / compliance content key. AFH uses the shared ALF pack
 * until Rules has a dedicated adult_family_home catalog.
 */
export function contentFacilityTypeSlugForFacts(
  slug?: string | null,
): string | undefined {
  const s = nonemptyString(slug).toLowerCase().replace(/-/g, "_");
  if (!s) return undefined;
  return CONTENT_SLUG_ALIASES[s] || s;
}

export function preferredFacilityName(facility: unknown): string {
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  return (
    nonemptyString(row.facility_name) ||
    nonemptyString(details.facility_name) ||
    nonemptyString(details.site_name)
  );
}

function preferredCity(facility: unknown): string {
  const details = preferredDetailsRecord(facility);
  return nonemptyString(asRecord(facility).city) || nonemptyString(details.city);
}

function preferredState(facility: unknown): string {
  const details = preferredDetailsRecord(facility);
  return nonemptyString(asRecord(facility).state) || nonemptyString(details.state);
}

function preferredAddress(facility: unknown): NameAddressFacility {
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  return {
    facility_name: preferredFacilityName(facility),
    address_1:
      nonemptyString(row.address_1) ||
      nonemptyString(details.address_1) ||
      nonemptyString(asRecord(details.primary_address).address_1),
    city: preferredCity(facility),
    state: preferredState(facility),
    postal_code:
      nonemptyString(row.postal_code) || nonemptyString(details.postal_code),
  };
}

/** True when Hub used the user-preferred PK as facility_id (no Rules mint). */
export function isHubSynthesizedSelfReportedRow(facility: unknown): boolean {
  if (!isSelfReportedPreferredFacility(facility)) return false;
  const details = preferredDetailsRecord(facility);
  const row = asRecord(facility);
  const ccn = nonemptyString(row.ccn_number) || nonemptyString(details.ccn_number);
  if (/^(license|selfreported)_\d+$/i.test(ccn)) return true;
  const rulesId = finiteId(row.facility_id) ?? finiteId(details.facility_id);
  const upfPk = resolveCheckoutPreferredFacilityPk(facility);
  return upfPk != null && (rulesId == null || rulesId === upfPk);
}

export function preferredFacilityMatchesExisting(
  preferred: unknown,
  existing: Array<NameAddressFacility & { facility_id?: number; id?: number }>,
): boolean {
  const details = preferredDetailsRecord(preferred);
  const rulesId = finiteId(details.facility_id) ?? finiteId(asRecord(preferred).facility_id);
  if (rulesId != null && existing.some((row) => (row.facility_id ?? row.id) === rulesId)) {
    return true;
  }
  const candidate = preferredAddress(preferred);
  if (!candidate.facility_name) return false;
  return existing.some((row) => isSameNameAndAddress(candidate, row));
}

export type SelectablePreferredSource = {
  id?: number | null;
  user_preferred_facility_id?: number | null;
  facility_id?: number | null;
  facility_name?: string | null;
  facility_type?: string | null;
  facility_type_id?: number | null;
  facility_type_slug?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  country_code?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  postal_code?: string | null;
  phone_number?: string | null;
  skip_npi_validation?: boolean;
  verification_source?: string | null;
  state_license_number?: string | null;
  is_verified?: boolean | null;
  is_active?: boolean | null;
  vertical?: string | null;
  facility_details?: unknown;
};

function streetLine(source: SelectablePreferredSource): string {
  const details = preferredDetailsRecord(source);
  return (
    nonemptyString(source.address_1) ||
    nonemptyString(details.address_1) ||
    nonemptyString(asRecord(details.primary_address).address_1)
  );
}

function localityLine(source: SelectablePreferredSource): string {
  const city = preferredCity(source);
  const state = preferredState(source);
  return [city, state].filter(Boolean).join(", ");
}

/** Map a self-reported preferred / session blob to a selectable my-facilities row. */
export function preferredFacilityToSelectableRow(
  source: SelectablePreferredSource,
): MyFacilityListRow | null {
  const name = preferredFacilityName(source);
  if (!name) return null;
  const details = preferredDetailsRecord(source);
  const preferredPk =
    resolveCheckoutPreferredFacilityPk(source) ?? finiteId(source.id);
  const rulesId = finiteId(source.facility_id) ?? finiteId(details.facility_id);
  const facilityId = rulesId ?? preferredPk;
  if (facilityId == null) return null;

  const slug =
    selfReportedFacilityTypeSlug(source) ||
    nonemptyString(source.facility_type_slug);
  const typeName =
    nonemptyString(source.facility_type) ||
    nonemptyString(details.facility_type) ||
    (slug ? slug.replace(/_/g, " ") : "Facility");
  const city = preferredCity(source);
  const state = preferredState(source);
  const street = streetLine(source);
  const postal =
    nonemptyString(source.postal_code) || nonemptyString(details.postal_code);
  const country =
    nonemptyString(source.country_code) ||
    nonemptyString(source.country) ||
    nonemptyString(details.country) ||
    "USA";
  const license = selfReportedLicenseNumber(source);
  const uniqueId = selfReportedUniqueId(preferredPk, license);
  const vertical =
    nonemptyString(source.vertical) ||
    nonemptyString(details.vertical) ||
    "healthcare";

  const targeting = asRecord(details.profile_targeting_json);
  if (license && !targeting.state_license_number) {
    targeting.state_license_number = license;
  }
  if (slug && !targeting.facility_type_slug) {
    targeting.facility_type_slug = slug;
  }
  targeting.verification_source = SELF_REPORTED_VERIFICATION_SOURCE;
  targeting.skip_npi_validation = true;

  return {
    id: facilityId,
    facility_id: facilityId,
    facility_name: name,
    city,
    state,
    full_address: [street, localityLine(source), postal].filter(Boolean).join(", "),
    facility_type: typeName,
    facility_type_slug: slug || undefined,
    vertical,
    org_id: 0,
    org_name: name,
    ccn_number: uniqueId || null,
    npi_number: null,
    address_1: street || null,
    address_2:
      nonemptyString(source.address_2) ||
      nonemptyString(details.address_2) ||
      null,
    postal_code: postal || null,
    country_code: country,
    primary_profile_slug: slug || null,
    profile_targeting_json: targeting,
    is_plan_active: source.is_verified === true ? true : undefined,
    plan_status: source.is_verified === true ? "active" : undefined,
    is_restricted: false,
  };
}

export function selectableRowFromPostPurchase(
  selection: PostPurchaseFacilitySelection | null | undefined,
  preferredSession?: SelectablePreferredSource | null,
): MyFacilityListRow | null {
  const sessionRow = preferredSession
    ? preferredFacilityToSelectableRow(preferredSession)
    : null;
  if (sessionRow) {
    if (selection?.facilityId && !preferredSession?.facility_id) {
      return { ...sessionRow, facility_id: selection.facilityId, id: selection.facilityId };
    }
    return sessionRow;
  }
  const name = nonemptyString(selection?.facilityName);
  if (!name) return null;
  const slug = nonemptyString(selection?.facilityTypeSlug);
  const city = nonemptyString(selection?.facilityCity);
  const facilityId = selection?.facilityId ?? null;
  if (facilityId == null && !slug) return null;
  return {
    id: facilityId ?? 0,
    facility_id: facilityId ?? 0,
    facility_name: name,
    city,
    state: "",
    full_address: city,
    facility_type: slug ? slug.replace(/_/g, " ") : "Facility",
    facility_type_slug: slug || undefined,
    vertical: selection?.vertical || "healthcare",
    org_id: 0,
    org_name: name,
    npi_number: null,
    country_code: "USA",
    primary_profile_slug: slug || null,
    profile_targeting_json: {
      verification_source: SELF_REPORTED_VERIFICATION_SOURCE,
      skip_npi_validation: true,
      ...(slug ? { facility_type_slug: slug } : {}),
    },
    is_plan_active: true,
    plan_status: "active",
    is_restricted: false,
  };
}

export function mergeSelfReportedPreferredIntoFacilities(
  facilities: MyFacilityListRow[],
  preferred: Array<UserPreferredFacility | SelectablePreferredSource>,
  extras: Array<MyFacilityListRow | null | undefined> = [],
): MyFacilityListRow[] {
  const merged: MyFacilityListRow[] = [...facilities];
  const consider = preferred.filter((row) =>
    isSelfReportedPreferredFacility(row),
  );

  for (const row of consider) {
    if (preferredFacilityMatchesExisting(row, merged)) continue;
    // Purchased / verified self-report should be selectable even if Rules
    // never minted a my-facilities row (missing NPI). Unpaid stay in Pending.
    const verified =
      (row as UserPreferredFacility).is_verified === true ||
      String((row as UserPreferredFacility).status || "").toLowerCase() ===
        "verified";
    if (!verified) continue;
    const synthetic = preferredFacilityToSelectableRow(row);
    if (synthetic) merged.push(synthetic);
  }

  for (const extra of extras) {
    if (!extra?.facility_name) continue;
    if (preferredFacilityMatchesExisting(extra, merged)) continue;
    if (
      extra.facility_id > 0 &&
      merged.some((row) => row.facility_id === extra.facility_id)
    ) {
      continue;
    }
    merged.push(extra);
  }

  return merged;
}

